/**
 * AseguradoActivityCard — Muestra en la barra lateral del expediente la
 * actividad que el asegurado / perjudicado ha generado a través de las
 * landings públicas (mensajes, archivos subidos y respuestas de
 * formularios). Se muestra un badge con el número de mensajes sin leer
 * y se pueden marcar como leídos en bloque.
 *
 * Endpoints usados (autenticados):
 *   GET  /api/expedientes/{id}/landing-activity
 *   POST /api/expedientes/{id}/landing-activity/mark-read
 */
import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  ChatCircleText, Paperclip, CheckCircle, ClipboardText, ArrowsClockwise,
  ImageSquare, FileText,
} from "@phosphor-icons/react";

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function ActivityItem({ a }) {
  const isUnread = !a.read_at;
  const baseCls = `text-[12px] rounded-md border px-2.5 py-2 ${
    isUnread ? "bg-amber-50 border-amber-200" : "bg-white border-border"
  }`;
  const ts = (
    <div className="text-[10px] text-muted font-mono mb-0.5 flex items-center gap-1.5">
      {fmtDateTime(a.created_at)}
      {isUnread && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" aria-label="Sin leer" />
      )}
    </div>
  );

  if (a.kind === "message") {
    return (
      <li className={baseCls} data-testid={`asegurado-activity-${a.id}`}>
        {ts}
        <div className="flex items-start gap-1.5">
          <ChatCircleText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
          <div className="text-foreground whitespace-pre-line break-words">{a.text}</div>
        </div>
      </li>
    );
  }

  if (a.kind === "upload") {
    const isImg = (a.mime_type || "").startsWith("image/");
    return (
      <li className={baseCls} data-testid={`asegurado-activity-${a.id}`}>
        {ts}
        <div className="flex items-start gap-1.5">
          {isImg
            ? <ImageSquare className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" weight="fill" />
            : <Paperclip className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" weight="fill" />
          }
          <div className="min-w-0 flex-1">
            <div className="text-foreground font-medium truncate">{a.file_name}</div>
            <div className="text-[10px] text-muted mt-0.5">{fmtBytes(a.file_size || 0)}</div>
            {a.text && <div className="text-[11px] text-muted italic mt-1 break-words">"{a.text}"</div>}
            <a
              href={`${API}/landings/uploads/${a.id}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-brand hover:underline mt-1 inline-block"
              data-testid={`asegurado-activity-open-${a.id}`}
            >
              Abrir archivo →
            </a>
          </div>
        </div>
      </li>
    );
  }

  if (a.kind === "form_response") {
    const entries = Object.entries(a.form_data || {});
    return (
      <li className={baseCls} data-testid={`asegurado-activity-${a.id}`}>
        {ts}
        <div className="flex items-start gap-1.5">
          <FileText className="w-3.5 h-3.5 text-violet-600 flex-shrink-0 mt-0.5" weight="fill" />
          <div className="min-w-0 flex-1">
            <div className="text-foreground font-medium">Formulario enviado</div>
            {entries.length > 0 && (
              <ul className="mt-1 space-y-0.5 list-none">
                {entries.map(([k, v]) => (
                  <li key={k} className="text-[11px] text-muted break-words">
                    <span className="font-semibold">{k}:</span> {String(v) || <em>vacío</em>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </li>
    );
  }

  return null;
}

export default function AseguradoActivityCard({ expedienteId, onUnreadChange }) {
  const [data, setData] = useState({ items: [], unread: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!expedienteId) return;
    setLoading(true);
    try {
      const r = await api.get(`/expedientes/${expedienteId}/landing-activity`);
      setData(r.data || { items: [], unread: 0, total: 0 });
      onUnreadChange?.(r.data?.unread || 0);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("[asegurado-activity] load:", e);
    } finally {
      setLoading(false);
    }
  }, [expedienteId, onUnreadChange]);

  useEffect(() => { load(); }, [load]);

  const markRead = async () => {
    try {
      await api.post(`/expedientes/${expedienteId}/landing-activity/mark-read`);
      toast.success("Mensajes marcados como leídos");
      await load();
    } catch (e) {
      toast.error("No se pudo marcar como leído");
    }
  };

  // Si no hay nada, no renderizamos la tarjeta para no añadir ruido.
  if (!loading && data.total === 0) return null;

  const visible = expanded ? data.items : data.items.slice(0, 4);

  return (
    <section
      className="bg-white border border-border rounded-lg overflow-hidden"
      data-testid="asegurado-activity-card"
    >
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border/70">
        <span className="icon-chip chip-icon-amber flex-shrink-0">
          <ClipboardText className="w-4 h-4" weight="regular" />
        </span>
        <h3 className="section-title flex-1">Página privada del asegurado</h3>
        {data.unread > 0 && (
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
            data-testid="asegurado-activity-unread-badge"
          >
            {data.unread} nuevo{data.unread === 1 ? "" : "s"}
          </span>
        )}
        <button
          onClick={load}
          title="Refrescar"
          className="text-muted hover:text-foreground"
          data-testid="asegurado-activity-refresh"
        >
          <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" />
        </button>
      </header>

      <div className="p-3 space-y-2">
        {loading && data.items.length === 0 && (
          <div className="text-[12px] text-muted text-center py-2">Cargando…</div>
        )}
        {data.items.length > 0 && (
          <ul className="space-y-1.5" data-testid="asegurado-activity-list">
            {visible.map((a) => <ActivityItem key={a.id} a={a} />)}
          </ul>
        )}
        {data.items.length > 4 && !expanded && (
          <button
            onClick={() => setExpanded(true)}
            className="text-[11px] text-brand hover:underline w-full text-center pt-1"
            data-testid="asegurado-activity-expand"
          >
            Ver {data.items.length - 4} más
          </button>
        )}
        {data.unread > 0 && (
          <button
            onClick={markRead}
            className="w-full mt-1 inline-flex items-center justify-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md bg-foreground text-white hover:bg-foreground/90"
            data-testid="asegurado-activity-mark-read"
          >
            <CheckCircle className="w-3.5 h-3.5" weight="bold" />
            Marcar como leído
          </button>
        )}
      </div>
    </section>
  );
}
